/* eslint-disable complexity */
import { Language } from "@faire/web-api/indigofair/languages/Language";
import { ILocaleKey } from "@faire/web-api/indigofair/locale/ILocaleKey";
import { getGlobalProperty } from "@faire/web/common/globals/getGlobalProperty";
import { MediumFormatting } from "@faire/web/common/localization/formatting";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web/common/localization/localize";
import { formatMoney, RoundingStrategy } from "@faire/web/common/money";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";

import { useFaireDirectBrand } from "@faire/retailer/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import { useFaireDirectFirstOrderIncentive } from "@faire/retailer/hooks/FaireDirect/useFaireDirectFirstOrderIncentive/useFaireDirectFirstOrderIncentive";
import { getTotalBrandCount } from "@faire/retailer/lib/getMarketingCount";
import { useIsSettingSignUpImageryTreatment } from "@faire/retailer/settings/useSettingDesktopSignUpModalImagery";
import { useOpenWithFaireStore } from "@faire/retailer/stores/domain/OpenWithFaireStore";

import { AppSignUpModalVariant } from "../AppSignUpModalVariant";

interface IProps {
  modalVariant: AppSignUpModalVariant;
  isOpenWithFaire?: boolean;
  isFaireMarket?: boolean;
  livestream?: boolean;
  align?: "left" | "center";
}

export const Subtitle: React.FC<IProps> = (props) => {
  const { faireDirectBrand } = useFaireDirectBrand();
  const externalSalesRepName: string | undefined = getGlobalProperty(
    "externalSalesRepName"
  );
  const localeKey: ILocaleKey | undefined = getGlobalProperty("localeKey");

  const {
    isFaireDirectCreditPercentageBased,
    formattedPercentageBasedNumber,
    formattedFixedBasedAmount,
  } = useFaireDirectFirstOrderIncentive();
  const { offeredMaxTerms } = useOpenWithFaireStore(["offeredMaxTerms"]);
  const isSettingSignUpImageryTreatment = useIsSettingSignUpImageryTreatment();

  const shortSubtitle = (
    <Typography align={props.align ?? "center"}>
      <StrictLocalMsg
        defaultMessage="Purchase from over {brandCountNumber} unique brands."
        values={{
          brandCountNumber: getTotalBrandCount(),
        }}
        description={{
          text: "Subtitle text on retailer sign-up modal.",
          img: "https://cdn.faire.com/fastly/8574ba6124fe545ec21985fe6607adf33aedd7c63d519f116163cde1cd92e11d.png",
        }}
      />
    </Typography>
  );

  if (props.modalVariant === AppSignUpModalVariant.FAIRE_DIRECT) {
    const promoMoney = formattedFixedBasedAmount;
    const salesPersonName = externalSalesRepName;
    const brandCompanyName = faireDirectBrand?.name;
    if (isFaireDirectCreditPercentageBased) {
      return (
        <Typography
          align={props.align ?? "center"}
          data-test-id="faire-direct-percentage-based-subtitle"
        >
          {salesPersonName && brandCompanyName ? (
            <>
              <StrictLocalMsg
                defaultMessage="{salesPersonName} invited you to shop {brandCompanyName} on Faire."
                description={{
                  text: "This is on a retailer sign up modal. If the retailer is sign up through a sales rep link for the brand, they will see this sentence.",
                  img: "https://cdn.faire.com/fastly/9698ae4a6057f3c44197d5453d93c7cc5397ea2b437fe828e4f7b3a19429dd9d.png",
                }}
                values={{
                  salesPersonName,
                  brandCompanyName,
                }}
              />
              &nbsp;
            </>
          ) : null}
          <StrictLocalMsg
            defaultMessage="Get {faireDirectPercentageDiscountNumber} off and 1 year of free shipping*"
            description={{
              text: "Discount promo message. To be explicit: The user will get percentage off discount on their first order with this brand. The money is covered by Faire and not the brand. Also, the asterisk refers to a disclaimer",
              img: "https://cdn.faire.com/fastly/5600f8641ce0789e4b4a134b04e8e6a8d03ded5a2d4af8cc6151cea03e8ad02f.png",
            }}
            values={{
              faireDirectPercentageDiscountNumber:
                formattedPercentageBasedNumber,
            }}
          />
        </Typography>
      );
    }

    if (salesPersonName && brandCompanyName) {
      return (
        <Typography align={props.align ?? "center"}>
          <StrictLocalMsg
            defaultMessage="{salesPersonName} invited you to shop {brandCompanyName} on Faire! Join today and get <MediumFormatting>{promoMoney} off</MediumFormatting> and <MediumFormatting>1 year of free shipping</MediumFormatting>, covered by Faire.*"
            description={{
              text: "Discount promo message. To be explicit: The user will get money off, and not be paying. The money is covered by Faire and not the brand. Also, the asterisk refers to a disclaimer",
              img: "https://cdn.faire.com/fastly/746f0d44d194b571240a95a7c30da58895940eb1c6b62ab56641b04cc5b7ab1b.png",
            }}
            values={{
              MediumFormatting,
              promoMoney: formattedFixedBasedAmount,
              salesPersonName,
              brandCompanyName,
            }}
          />
        </Typography>
      );
    }
    return (
      <Typography align={props.align ?? "center"}>
        <StrictLocalMsg
          defaultMessage="Get {promoMoney} off and 1 year of free shipping*"
          description={{
            text: "Discount promo message. To be explicit: The user will get money off, and not be paying. The money is covered by Faire and not the brand. Also, the asterisk refers to a disclaimer",
            img: "https://cdn.faire.com/fastly/be9d29cdc8ea026cee7c9715f4e6954bcc0f741d4badc23dbb9b479ca9ad85c4.png",
          }}
          values={{
            promoMoney,
          }}
        />
      </Typography>
    );
  } else if (
    props.modalVariant === AppSignUpModalVariant.FAIRE_DIRECT_EMAIL_INVITATION
  ) {
    return (
      <Typography align={props.align ?? "center"}>
        <StrictLocalMsg defaultMessage="Plus, join thousands of retailers shopping unique brands on the Faire marketplace." />
      </Typography>
    );
  } else if (props.modalVariant === AppSignUpModalVariant.FACEBOOK) {
    if (isSettingSignUpImageryTreatment) {
      return shortSubtitle;
    } else {
      return (
        <Typography align={props.align ?? "center"}>
          <StrictLocalMsg
            defaultMessage="Purchase from over {brandCount} brands on the Faire marketplace. Sign up for free!"
            values={DEPRECATED_backwardsCompatibleValues({
              brandCount: getTotalBrandCount(),
            })}
          />
        </Typography>
      );
    }
  } else if (props.modalVariant === AppSignUpModalVariant.PRODUCT) {
    if (isSettingSignUpImageryTreatment) {
      return shortSubtitle;
    } else {
      return (
        <Typography align={props.align ?? "center"}>
          <ResponsiveContentV2
            noJS
            tabletAndAbove={
              <StrictLocalMsg
                defaultMessage="Purchase from over {brandCount} brands on Faire. Sign up for free!"
                values={DEPRECATED_backwardsCompatibleValues({
                  brandCount: getTotalBrandCount(),
                })}
              />
            }
            mobile={
              <StrictLocalMsg
                defaultMessage="Purchase from over {brandCount} brands. Sign up for free!"
                values={DEPRECATED_backwardsCompatibleValues({
                  brandCount: getTotalBrandCount(),
                })}
              />
            }
          />
        </Typography>
      );
    }
  } else if (props.isOpenWithFaire) {
    const maxLimitAmount = formatMoney({
      money: offeredMaxTerms,
      roundingStrategy: RoundingStrategy.DROP_CENTS,
    });
    return (
      <Typography align={props.align ?? "center"}>
        <ResponsiveContentV2
          noJS
          tabletAndAbove={
            <StrictLocalMsg
              defaultMessage="Get up to {maxLimitAmount} in inventory from thousands of brands for your new store."
              values={DEPRECATED_backwardsCompatibleValues({ maxLimitAmount })}
            />
          }
          mobile={
            <StrictLocalMsg
              defaultMessage="Get up to {maxLimitAmount} in inventory from thousands of unique brands for your new store."
              values={DEPRECATED_backwardsCompatibleValues({ maxLimitAmount })}
            />
          }
        />
      </Typography>
    );
  } else if (props.livestream) {
    return (
      <Typography align={props.align ?? "center"}>
        <StrictLocalMsg defaultMessage="Sign up to access exclusive industry livestream content!" />
      </Typography>
    );
  } else if (props.isFaireMarket) {
    return (
      <Typography align={props.align ?? "center"}>
        <StrictLocalMsg
          // eslint-disable-next-line @faire/no-mid-sentence-linebreaks
          defaultMessage="Sign up as a retailer on Faire to join our{lineBreak}upcoming virtual trade shows and start{lineBreak}shopping wholesale today!"
          values={DEPRECATED_backwardsCompatibleValues({ lineBreak: <br /> })}
        />
      </Typography>
    );
  } else if (
    localeKey?.language === Language.FRA ||
    localeKey?.language === Language.DEU
  ) {
    return (
      <Typography align={props.align ?? "center"}>
        <StrictLocalMsg
          defaultMessage="High-quality brands for your customers. The registration is free."
          description={{
            text: "French translate to: Découvrez des marques uniques et de qualité qui plairont à coup sûr à vos clients. L’inscription est gratuite ! | German translate to: Hochwertige Marken für deine Kund*innen. Die Anmeldung ist kostenlos.",
          }}
        />
      </Typography>
    );
  } else if (isSettingSignUpImageryTreatment) {
    return shortSubtitle;
  }

  return (
    <Typography align={props.align ?? "center"}>
      <ResponsiveContentV2
        noJS
        tabletAndAbove={
          <StrictLocalMsg
            defaultMessage="Purchase from over {brandCount} unique brands. Sign up for free!"
            values={DEPRECATED_backwardsCompatibleValues({
              brandCount: getTotalBrandCount(),
            })}
          />
        }
        mobile={
          <StrictLocalMsg
            defaultMessage="Purchase from over {brandCount} brands. Sign up for free!"
            values={DEPRECATED_backwardsCompatibleValues({
              brandCount: getTotalBrandCount(),
            })}
          />
        }
      />
    </Typography>
  );
};
